import {
  Drawer,
  Stack,
  Box,
  useTheme,
  Typography,
  Button,
  IconButton,
} from "@mui/material";
import SideNavSection from "./SideNavSection";
import { usePathname } from "hooks/use-pathname";
import { useMemo, useState } from "react";
import Dark_Theme_Icon from "assets/icons1/dark_theme_mailzzy_collasped_logo.svg";
import LightTheme_Icon from "assets/icons1/light_theme_mailzzy_collasped_logo.svg";
import Dark_Theme_Logo from "assets/images/dark_side_nav_mailzzy_logo.svg";
import Light_Theme_Logo from "assets/images/light_side_nav_mailzzy_logo.svg";
import history from "utils/history";
import { useAuthRouteRedirection } from "../../Config";
import { useAppDispatch, useAppSelector } from "utils/hooks";
import {
  selectSideNavCollpased,
  selectTheme,
  updateSideNavCollpased,
} from "redux/preferencesSlice";
import ArrowLeft from "assets/icons1/arrow_left.svg";
import sideNavStyles from "layouts/DrawerLayout/components/SideNav.Styles";

const SIDE_NAV_WIDTH = 280;
const SIDE_NAV_COLLAPSED_WIDTH = 70;

const useCssVars = (): Record<string, string> => {
  const themePreference = useAppSelector(selectTheme);
  const theme = useTheme();

  return useMemo((): Record<string, string> => {
    if (themePreference === "dark") {
      return {
        "--nav-bg": "#21262D",
        "--nav-color": theme.palette.neutral[100],
        "--nav-divider-color": theme.palette.neutral[800],
        "--nav-border-color": "#21262D",
        "--nav-logo-border": theme.palette.neutral[700],
        "--nav-item-color": "#818288",
        "--nav-item-hover-bg": "rgba(255, 255, 255, 0.04)",
        "--nav-item-hover-color": "#FFFFFF",
        "--nav-item-active-bg": theme.palette.primary.main,
        "--nav-item-disabled-color": theme.palette.neutral[600],
        "--nav-item-icon-color": "#818288",
        "--nav-item-icon-active-color": "#FFFFFF",
        "--nav-item-icon-disabled-color": theme.palette.neutral[700],
        "--nav-item-chevron-color": theme.palette.neutral[700],
        "--nav-scrollbar-color": theme.palette.neutral[400],
        "--nav-item-active-color": "#FFFFFF",
      };
    } else {
      return {
        "--nav-bg": "#FFFFFF",
        "--nav-color": "#FFFFFF",
        "--nav-divider-color": theme.palette.divider,
        "--nav-border-color": "#F2EEFA",
        "--nav-logo-border": theme.palette.neutral[100],
        "--nav-section-title-color": theme.palette.neutral[400],
        "--nav-item-color": "#9CA0B2",
        "--nav-item-hover-bg": theme.palette.primary.main,
        "--nav-item-active-bg": theme.palette.primary.main,
        "--nav-item-active-color": "#000000",
        "--nav-item-disabled-color": theme.palette.neutral[400],
        "--nav-item-icon-color": "#757D90",
        "--nav-item-icon-active-color": "#000000",
        "--nav-item-icon-disabled-color": theme.palette.neutral[400],
        "--nav-item-chevron-color": theme.palette.neutral[400],
        "--nav-scrollbar-color": theme.palette.neutral[900],
      };
    }
  }, [theme]);
};

interface CustomProps {
  sections: any;
}

const SideNav = (props: CustomProps) => {
  const classes = sideNavStyles;
  const dispatch = useAppDispatch();
  const pathname = usePathname();
  const cssVars = useCssVars();
  const authRoute = useAuthRouteRedirection();
  const themePreference = useAppSelector(selectTheme);
  const sideNavCollpased = useAppSelector(selectSideNavCollpased);
  const isDarkTheme = themePreference === "dark";

  const toggleNav = () => {
    dispatch(updateSideNavCollpased(!sideNavCollpased));
  };

  return (
    <Box>
      <Drawer
        anchor="left"
        open
        PaperProps={{
          sx: {
            ...cssVars,
            backgroundColor: "var(--nav-bg)",
            borderRightColor: "var(--nav-border-color)",
            borderRightStyle: "solid",
            borderRightWidth: 1,
            color: "var(--nav-color)",
            width: sideNavCollpased ? SIDE_NAV_COLLAPSED_WIDTH : SIDE_NAV_WIDTH,
            overflowX: "hidden",
            transition: "width 0.3s ease-in-out",
          },
        }}
        variant="permanent"
      >
        <Stack sx={{ height: "100%" }}>
          <Stack
            direction="row"
            spacing={2}
            sx={{ px: sideNavCollpased ? 2 : 3, pt: 3 }}
          >
            <Box
              onClick={() => history.push(authRoute)}
              sx={
                sideNavCollpased
                  ? classes.mailzzyLogoBoxCollapsed
                  : classes.mailzzyLogoBox
              }
            >
              {!sideNavCollpased ? (
                <img
                  src={isDarkTheme ? Dark_Theme_Logo : Light_Theme_Logo}
                  alt="mailzzy_logo"
                  width="180px"
                />
              ) : (
                <img
                  src={isDarkTheme ? Dark_Theme_Icon : LightTheme_Icon}
                  alt="mailzzy_icon"
                  width="30px"
                />
              )}
            </Box>
          </Stack>
          <Stack
            component="nav"
            spacing={2}
            sx={{
              flexGrow: 1,
              px: 2,
              pt: 6,
              transition: "padding 0.3s ease-in-out",
            }}
          >
            {props.sections.map((section: any, index: number) => (
              <SideNavSection
                items={section.items}
                key={index}
                pathname={pathname}
                subheader={section.subheader}
                isCollapsed={sideNavCollpased}
              />
            ))}
          </Stack>
        </Stack>
      </Drawer>
      <IconButton
        onClick={toggleNav}
        color="primary"
        sx={{
          ...classes.collpaseIconButton,
          position: "fixed",
          top: "15px",
          left: sideNavCollpased
            ? `${SIDE_NAV_COLLAPSED_WIDTH}px`
            : `${SIDE_NAV_WIDTH - 3}px`,
          backgroundColor: isDarkTheme ? "#21262D" : "#FFFFFF",
          border: isDarkTheme ? "1px solid #8F92A1" : "1px solid #F2EEFA",
          "&:hover": {
            backgroundColor: isDarkTheme ? "#21262D" : "#FFFFFF",
          },
          width: "28px",
          height: "28px",
        }}
      >
        <img
          src={ArrowLeft}
          width={"8px"}
          height={"8px"}
          alt="arrow"
          style={{
            transition: "transform 0.3s ease-in-out",
            transform: sideNavCollpased ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </IconButton>
    </Box>
  );
};

export default SideNav;
